import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useSectionNav } from "@/lib/useSectionNav";
import { SERVICES, getServiceBySlug } from "@/data/services";

const APPROACH_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/be4b6ead-eecf-46db-b204-8360a1346120/images/4299bdafe23b6da2ec02d00e152451f3be752cdba9d87b31038f255857720bfc.jpeg";

const pad = (n) => String(n).padStart(2, "0");

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const goNav = useSectionNav();

  if (!service) return <Navigate to="/" replace />;

  const Icon = service.icon;
  const index = SERVICES.findIndex((s) => s.slug === service.slug);
  const others = SERVICES.filter((s) => s.slug !== service.slug);

  return (
    <div className="bg-white min-h-screen" data-testid="service-detail-page">
      <Navbar />

      {/* HERO */}
      <section
        data-testid="service-detail-hero"
        className="relative min-h-[68vh] flex items-end overflow-hidden bg-navy-900"
      >
        <img
          src={service.heroImage}
          alt={service.title}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 eb-hero-overlay" />
        <div className="absolute inset-0 eb-grain" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 w-full pt-32 pb-14">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-silver-300/70">
            <Link to="/" data-testid="breadcrumb-home" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <button onClick={() => goNav("#services")} data-testid="breadcrumb-services" className="hover:text-white transition-colors">Services</button>
            <span>/</span>
            <span className="text-white">{service.title}</span>
          </div>

          <div className="mt-6 flex items-center gap-3 text-white/70">
            <Icon size={18} className="text-brand-blue" />
            <span className="eb-overline">Service {pad(index + 1)} · {service.category}</span>
          </div>

          <h1 className="mt-5 max-w-3xl text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight font-black text-white">
            {service.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-silver-300/90 font-medium">
            {service.tagline}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="service-detail-cta"
              onClick={() => goNav("#contact")}
              className="group inline-flex items-center justify-center gap-2 rounded-sm bg-brand-blue px-7 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-white hover:text-navy-900 hover:-translate-y-0.5"
            >
              Request a Consultation
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => goNav("#services")}
              className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/25 px-7 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-white/10"
            >
              <ArrowLeft size={16} /> Back to all services
            </button>
          </div>
        </div>
      </section>

      {/* OVERVIEW + AT A GLANCE */}
      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-3 gap-12 lg:gap-16">
          <div className="lg:col-span-2">
            <Reveal><span className="eb-overline text-brand-blue">Overview</span></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 text-3xl sm:text-4xl lg:text-[2.5rem] leading-[1.1] tracking-tight font-black text-navy-900">
                {service.overviewHeading}
              </h2>
            </Reveal>
            {service.overview.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.05}>
                <p className="mt-6 text-base sm:text-lg leading-relaxed text-navy-700/80 font-medium">{p}</p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="lg:col-span-1">
            <div className="sticky top-28 border border-silver-200 bg-silver p-8">
              <span className="inline-flex h-12 w-12 items-center justify-center bg-navy-900 text-white">
                <Icon size={22} />
              </span>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-4xl font-black text-brand-blue tracking-tight">{service.stat.value}</span>
                <span className="text-xs uppercase tracking-[0.12em] font-semibold text-navy-700/60">{service.stat.label}</span>
              </div>
              <div className="mt-7 eb-overline text-silver-400">What&apos;s included</div>
              <ul className="mt-4 space-y-3">
                {service.capabilities.map((c) => (
                  <li key={c.title} className="flex gap-2.5 text-sm font-medium text-navy-900">
                    <Check size={17} className="mt-0.5 shrink-0 text-brand-blue" />
                    {c.title}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => goNav("#contact")}
                className="mt-7 w-full rounded-sm bg-brand-blue py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-navy-900"
              >
                Request a Consultation
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="bg-silver py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><span className="eb-overline text-brand-blue">What we deliver</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 max-w-2xl text-3xl sm:text-4xl lg:text-[2.5rem] leading-[1.1] tracking-tight font-black text-navy-900">
              Capabilities that move the needle.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {service.capabilities.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05} className="h-full">
                <div className="h-full bg-white border border-silver-200 p-7 flex flex-col">
                  <span className="text-sm font-black text-silver-300 tracking-tight">{pad(i + 1)}</span>
                  <h3 className="mt-4 text-lg font-bold text-navy-900 tracking-tight">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy-700/70">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH / PROCESS */}
      <section className="relative bg-navy-900 py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 eb-grain opacity-60" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><span className="eb-overline text-brand-blue">Our approach</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 max-w-2xl text-3xl sm:text-4xl lg:text-[2.5rem] leading-[1.1] tracking-tight font-black text-white">
              How we work with you.
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
            {service.process.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08} className="border-b border-r border-white/10">
                <div className="h-full p-8">
                  <span className="text-3xl font-black text-white/15">{pad(i + 1)}</span>
                  <h3 className="mt-5 text-lg font-bold text-white tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-silver-300/70">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="relative order-2 lg:order-1">
            <div className="absolute -bottom-5 -left-4 h-[85%] w-[75%] bg-silver-200 hidden sm:block" aria-hidden />
            <img
              src={APPROACH_IMAGE}
              alt="Echo Bravo advisory team in discussion"
              className="relative z-10 w-full h-[380px] sm:h-[440px] object-cover"
              loading="lazy"
            />
          </Reveal>
          <div className="order-1 lg:order-2">
            <Reveal><span className="eb-overline text-brand-blue">The outcome</span></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 text-3xl sm:text-4xl lg:text-[2.5rem] leading-[1.1] tracking-tight font-black text-navy-900">
                What you can expect.
              </h2>
            </Reveal>
            <ul className="mt-8 space-y-4">
              {service.outcomes.map((o, i) => (
                <Reveal key={i} delay={0.08 + i * 0.05}>
                  <li className="flex gap-3">
                    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center bg-brand-blue text-white">
                      <Check size={14} />
                    </span>
                    <span className="text-base font-medium leading-relaxed text-navy-900">{o}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      <section className="bg-silver py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-end justify-between gap-6">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-black text-navy-900 tracking-tight">Explore more services</h2>
            </Reveal>
            <Reveal delay={0.05}>
              <button
                onClick={() => goNav("#services")}
                className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-navy-900 hover:text-brand-blue transition-colors"
              >
                View all <ArrowUpRight size={16} />
              </button>
            </Reveal>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {others.map((s, i) => {
              const RIcon = s.icon;
              return (
                <Reveal key={s.slug} delay={i * 0.05} className="h-full">
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`related-service-${s.slug}`}
                    className="group h-full flex flex-col bg-white border border-silver-200 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-blue/40 hover:shadow-[0_18px_40px_rgba(10,17,40,0.08)]"
                  >
                    <span className="inline-flex h-11 w-11 items-center justify-center bg-silver-200 text-navy-900 transition-colors duration-300 group-hover:bg-brand-blue group-hover:text-white">
                      <RIcon size={20} />
                    </span>
                    <h3 className="mt-5 text-lg font-bold text-navy-900 tracking-tight">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-navy-700/70 flex-1">{s.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-navy-900 transition-colors group-hover:text-brand-blue">
                      Learn more <ArrowUpRight size={15} />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-navy-900">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 border border-white/10 p-8 sm:p-12">
            <div className="max-w-2xl text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                Ready to discuss {service.title.toLowerCase()}?
              </h2>
              <p className="mt-3 text-silver-300/80">
                Speak with a senior Echo Bravo advisor about your fleet and objectives.
              </p>
            </div>
            <button
              data-testid="service-detail-cta-bottom"
              onClick={() => goNav("#contact")}
              className="shrink-0 inline-flex items-center gap-2 rounded-sm bg-brand-blue px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-white hover:text-navy-900"
            >
              Request a Consultation <ArrowRight size={17} />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
